import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixAgentsV1AgentDefinition from './agents-v1-agent-definition-agent-registry.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/** Registry entry describing an agent that can be offered on a site. */
export interface AgentDefinition {
  /**
   * AgentDefinition ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the AgentDefinition is updated.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the AgentDefinition was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the AgentDefinition was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Stable machine-readable key for the agent.
   * @minLength 1
   * @maxLength 120
   */
  key?: string;
  /**
   * Human-readable agent name.
   * @minLength 1
   * @maxLength 120
   */
  displayName?: string;
  /**
   * Short explanation of what the agent does.
   * @maxLength 1000
   */
  description?: string | null;
  /**
   * Platform or vendor that provides the agent.
   * @minLength 1
   * @maxLength 120
   */
  provider?: string;
  /**
   * Runtime used to execute the agent.
   * @minLength 1
   * @maxLength 120
   */
  runtime?: string;
  /**
   * Agent package or configuration version.
   * @maxLength 80
   */
  version?: string | null;
  /** Current lifecycle state of the registry entry. */
  status?: AgentStatusWithLiterals;
  /**
   * Capability identifiers supported by the agent.
   * @maxSize 100
   * @maxLength 120
   */
  capabilities?: string[];
  /** Data Extensions. */
  extendedFields?: ExtendedFields;
  /** Tags. */
  tags?: Tags;
}

export enum AgentStatus {
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  DEPRECATED = 'DEPRECATED',
}

/** @enumType */
export type AgentStatusWithLiterals =
  | AgentStatus
  | 'ACTIVE'
  | 'PAUSED'
  | 'DEPRECATED';

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

/**
 * Common object for tags.
 * Should be use as in this example:
 * message Foo {
 * option (.wix.api.decomposite_of) = "wix.commons.v2.tags.Foo";
 * string id = 1;
 * ...
 * Tags tags = 5
 * }
 *
 * example of taggable entity
 * {
 * id: "123"
 * tags: {
 * public_tags: {
 * tag_ids:["11","22"]
 * },
 * private_tags: {
 * tag_ids: ["33", "44"]
 * }
 * }
 * }
 */
export interface Tags {
  /** Tags that require an additional permission in order to access them, normally not given to site members or visitors. */
  privateTags?: TagList;
  /** Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors. */
  publicTags?: TagList;
}

export interface TagList {
  /**
   * List of tag IDs.
   * @maxSize 100
   * @maxLength 5
   */
  tagIds?: string[];
}

export interface AgentDefinitionTagsModified {
  /** Updated AgentDefinition. */
  agentDefinition?: AgentDefinition;
  /** Tags that were assigned to the AgentDefinition. */
  assignedTags?: Tags;
  /** Tags that were unassigned from the AgentDefinition. */
  unassignedTags?: Tags;
}

export interface CreateAgentDefinitionRequest {
  /** AgentDefinition to be created. */
  agentDefinition: AgentDefinition;
}

export interface CreateAgentDefinitionResponse {
  /** The created AgentDefinition. */
  agentDefinition?: AgentDefinition;
}

export interface GetAgentDefinitionRequest {
  /**
   * ID of the AgentDefinition to retrieve.
   * @format GUID
   */
  agentDefinitionId: string;
}

export interface GetAgentDefinitionResponse {
  /** The requested AgentDefinition. */
  agentDefinition?: AgentDefinition;
}

export interface UpdateAgentDefinitionRequest {
  /** AgentDefinition to be updated, may be partial. */
  agentDefinition: AgentDefinition;
  /**
   * Set of fields to update.
   * @internal
   */
  fieldMask?: string[];
}

export interface UpdateAgentDefinitionResponse {
  /** Updated AgentDefinition. */
  agentDefinition?: AgentDefinition;
}

export interface DeleteAgentDefinitionRequest {
  /**
   * ID of the AgentDefinition to delete.
   * @format GUID
   */
  agentDefinitionId: string;
}

export interface DeleteAgentDefinitionResponse {}

export interface QueryAgentDefinitionsRequest {
  /** WQL expression. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section).
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryAgentDefinitionsResponse {
  /** List of AgentDefinitions. */
  agentDefinitions?: AgentDefinition[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface BulkUpdateAgentDefinitionTagsRequest {
  /**
   * IDs of AgentDefinitions whose tags will be updated.
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  agentDefinitionIds: string[];
  /** Tags to assign. */
  assignTags?: Tags;
  /** Tags to unassign. */
  unassignTags?: Tags;
}

export interface BulkUpdateAgentDefinitionTagsResponse {
  /**
   * Results.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateAgentDefinitionTagsResult[];
  /** Metadata regarding the bulk update operation. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Provided only whenever possible. For example, `itemId` can't be provided when item creation has failed.
   * @format GUID
   */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action for this item was successful. When `false`, the `error` field is returned. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkUpdateAgentDefinitionTagsResult {
  /** Metadata regarding the specific single update operation. */
  itemMetadata?: ItemMetadata;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateAgentDefinitionTagsByFilterRequest {
  /** Filter that selects AgentDefinitions whose tags will be updated. */
  filter: Record<string, any> | null;
  /** Tags to assign. */
  assignTags?: Tags;
  /** Tags to unassign. */
  unassignTags?: Tags;
}

export interface BulkUpdateAgentDefinitionTagsByFilterResponse {
  /**
   * Job ID.
   * @format GUID
   */
  jobId?: string;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type BulkUpdateAgentDefinitionTagsApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type BulkUpdateAgentDefinitionTagsByFilterApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};

/**
 * Creates an AgentDefinition.
 * @param agentDefinition - AgentDefinition to be created.
 * @internal
 * @documentationMaturity preview
 * @requiredField agentDefinition
 * @requiredField agentDefinition.displayName
 * @requiredField agentDefinition.key
 * @requiredField agentDefinition.provider
 * @requiredField agentDefinition.runtime
 * @permissionId agents:v1:agent_definition:create_agent_definition
 * @returns The created AgentDefinition.
 * @fqn wix.agents.v1.AgentRegistryService.CreateAgentDefinition
 */
export async function createAgentDefinition(
  agentDefinition: NonNullablePaths<
    AgentDefinition,
    `displayName` | `key` | `provider` | `runtime`,
    0
  >
): Promise<
  NonNullablePaths<
    AgentDefinition,
    | `key`
    | `displayName`
    | `provider`
    | `runtime`
    | `status`
    | `capabilities`
    | `tags.privateTags.tagIds`,
    2
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    agentDefinition: agentDefinition,
  });

  const reqOpts =
    ambassadorWixAgentsV1AgentDefinition.createAgentDefinition(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.agentDefinition!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { agentDefinition: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['agentDefinition']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves an AgentDefinition.
 * @param agentDefinitionId - ID of the AgentDefinition to retrieve.
 * @internal
 * @documentationMaturity preview
 * @requiredField agentDefinitionId
 * @permissionId agents:v1:agent_definition:get_agent_definition
 * @returns The requested AgentDefinition.
 * @fqn wix.agents.v1.AgentRegistryService.GetAgentDefinition
 */
export async function getAgentDefinition(
  agentDefinitionId: string
): Promise<
  NonNullablePaths<
    AgentDefinition,
    | `key`
    | `displayName`
    | `provider`
    | `runtime`
    | `status`
    | `capabilities`
    | `tags.privateTags.tagIds`,
    2
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    agentDefinitionId: agentDefinitionId,
  });

  const reqOpts =
    ambassadorWixAgentsV1AgentDefinition.getAgentDefinition(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.agentDefinition!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { agentDefinitionId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['agentDefinitionId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates an AgentDefinition.
 * @param _id - AgentDefinition ID.
 * @internal
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField agentDefinition
 * @requiredField agentDefinition.revision
 * @permissionId agents:v1:agent_definition:update_agent_definition
 * @returns Updated AgentDefinition.
 * @fqn wix.agents.v1.AgentRegistryService.UpdateAgentDefinition
 */
export async function updateAgentDefinition(
  _id: string,
  agentDefinition: NonNullablePaths<UpdateAgentDefinition, `revision`, 0>
): Promise<
  NonNullablePaths<
    AgentDefinition,
    | `key`
    | `displayName`
    | `provider`
    | `runtime`
    | `status`
    | `capabilities`
    | `tags.privateTags.tagIds`,
    2
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    agentDefinition: { ...agentDefinition, id: _id },
  });

  const reqOpts =
    ambassadorWixAgentsV1AgentDefinition.updateAgentDefinition(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.agentDefinition!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { agentDefinition: '$[1]' },
        explicitPathsToArguments: { 'agentDefinition.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'agentDefinition']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateAgentDefinition {
  /**
   * AgentDefinition ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the AgentDefinition is updated.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the AgentDefinition was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the AgentDefinition was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Stable machine-readable key for the agent.
   * @minLength 1
   * @maxLength 120
   */
  key?: string;
  /**
   * Human-readable agent name.
   * @minLength 1
   * @maxLength 120
   */
  displayName?: string;
  /**
   * Short explanation of what the agent does.
   * @maxLength 1000
   */
  description?: string | null;
  /**
   * Platform or vendor that provides the agent.
   * @minLength 1
   * @maxLength 120
   */
  provider?: string;
  /**
   * Runtime used to execute the agent.
   * @minLength 1
   * @maxLength 120
   */
  runtime?: string;
  /**
   * Agent package or configuration version.
   * @maxLength 80
   */
  version?: string | null;
  /** Current lifecycle state of the registry entry. */
  status?: AgentStatusWithLiterals;
  /**
   * Capability identifiers supported by the agent.
   * @maxSize 100
   * @maxLength 120
   */
  capabilities?: string[];
  /** Data Extensions. */
  extendedFields?: ExtendedFields;
  /** Tags. */
  tags?: Tags;
}

/**
 * Deletes an AgentDefinition.
 * @param agentDefinitionId - ID of the AgentDefinition to delete.
 * @internal
 * @documentationMaturity preview
 * @requiredField agentDefinitionId
 * @permissionId agents:v1:agent_definition:delete_agent_definition
 * @fqn wix.agents.v1.AgentRegistryService.DeleteAgentDefinition
 */
export async function deleteAgentDefinition(
  agentDefinitionId: string
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    agentDefinitionId: agentDefinitionId,
  });

  const reqOpts =
    ambassadorWixAgentsV1AgentDefinition.deleteAgentDefinition(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { agentDefinitionId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['agentDefinitionId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of AgentDefinitions by WQL query.
 * @internal
 * @documentationMaturity preview
 * @permissionId agents:v1:agent_definition:query_agent_definitions
 * @fqn wix.agents.v1.AgentRegistryService.QueryAgentDefinitions
 */
export function queryAgentDefinitions(): AgentDefinitionsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    AgentDefinition,
    'CURSOR',
    QueryAgentDefinitionsRequest,
    QueryAgentDefinitionsResponse
  >({
    func: async (payload: QueryAgentDefinitionsRequest) => {
      const reqOpts =
        ambassadorWixAgentsV1AgentDefinition.queryAgentDefinitions(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryAgentDefinitionsRequest['query']) => {
      const args = [query, {}] as [QueryAgentDefinitionsRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryAgentDefinitionsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.agentDefinitions,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface AgentDefinitionsQueryResult extends QueryCursorResult {
  items: AgentDefinition[];
  query: AgentDefinitionsQueryBuilder;
  next: () => Promise<AgentDefinitionsQueryResult>;
  prev: () => Promise<AgentDefinitionsQueryResult>;
}

export interface AgentDefinitionsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  eq: (
    propertyName:
      | '_id'
      | 'key'
      | 'displayName'
      | 'provider'
      | 'runtime'
      | 'version'
      | 'status',
    value: any
  ) => AgentDefinitionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ne: (
    propertyName:
      | '_id'
      | 'key'
      | 'displayName'
      | 'provider'
      | 'runtime'
      | 'version'
      | 'status',
    value: any
  ) => AgentDefinitionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ge: (
    propertyName:
      | '_id'
      | 'key'
      | 'displayName'
      | 'provider'
      | 'runtime'
      | 'version',
    value: any
  ) => AgentDefinitionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  gt: (
    propertyName:
      | '_id'
      | 'key'
      | 'displayName'
      | 'provider'
      | 'runtime'
      | 'version',
    value: any
  ) => AgentDefinitionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  le: (
    propertyName:
      | '_id'
      | 'key'
      | 'displayName'
      | 'provider'
      | 'runtime'
      | 'version',
    value: any
  ) => AgentDefinitionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  lt: (
    propertyName:
      | '_id'
      | 'key'
      | 'displayName'
      | 'provider'
      | 'runtime'
      | 'version',
    value: any
  ) => AgentDefinitionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   * @documentationMaturity preview
   */
  startsWith: (
    propertyName:
      | '_id'
      | 'key'
      | 'displayName'
      | 'provider'
      | 'runtime'
      | 'version',
    value: string
  ) => AgentDefinitionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasSome: (
    propertyName: 'tags.privateTags.tagIds' | 'tags.publicTags.tagIds',
    value: any[]
  ) => AgentDefinitionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasAll: (
    propertyName: 'tags.privateTags.tagIds' | 'tags.publicTags.tagIds',
    value: any[]
  ) => AgentDefinitionsQueryBuilder;
  /** @documentationMaturity preview */
  in: (
    propertyName:
      | '_id'
      | 'key'
      | 'displayName'
      | 'provider'
      | 'runtime'
      | 'version'
      | 'status',
    value: any
  ) => AgentDefinitionsQueryBuilder;
  /** @documentationMaturity preview */
  exists: (
    propertyName:
      | '_id'
      | 'key'
      | 'displayName'
      | 'provider'
      | 'runtime'
      | 'version'
      | 'status'
      | 'tags.privateTags.tagIds'
      | 'tags.publicTags.tagIds',
    value: boolean
  ) => AgentDefinitionsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  ascending: (
    ...propertyNames: Array<
      | '_id'
      | 'key'
      | 'displayName'
      | 'provider'
      | 'runtime'
      | 'version'
      | 'status'
      | 'tags.privateTags.tagIds'
      | 'tags.publicTags.tagIds'
    >
  ) => AgentDefinitionsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  descending: (
    ...propertyNames: Array<
      | '_id'
      | 'key'
      | 'displayName'
      | 'provider'
      | 'runtime'
      | 'version'
      | 'status'
      | 'tags.privateTags.tagIds'
      | 'tags.publicTags.tagIds'
    >
  ) => AgentDefinitionsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object.
   * @documentationMaturity preview
   */
  limit: (limit: number) => AgentDefinitionsQueryBuilder;
  /** @param cursor - A pointer to specific record
   * @documentationMaturity preview
   */
  skipTo: (cursor: string) => AgentDefinitionsQueryBuilder;
  /** @documentationMaturity preview */
  find: () => Promise<AgentDefinitionsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.agents.v1.AgentRegistryService.QueryAgentDefinitions
 * @requiredField query
 */
export async function typedQueryAgentDefinitions(
  query: AgentDefinitionQuery
): Promise<
  NonNullablePaths<
    QueryAgentDefinitionsResponse,
    | `agentDefinitions`
    | `agentDefinitions.${number}.key`
    | `agentDefinitions.${number}.displayName`
    | `agentDefinitions.${number}.provider`
    | `agentDefinitions.${number}.runtime`
    | `agentDefinitions.${number}.status`,
    2
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixAgentsV1AgentDefinition.queryAgentDefinitions(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AgentDefinitionQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['tags.privateTags.tagIds', 'tags.publicTags.tagIds'];
      operators: ['$hasAll', '$hasSome'];
      sort: 'BOTH';
    },
    {
      fields: [
        '_id',
        'displayName',
        'key',
        'provider',
        'runtime',
        'status',
        'version'
      ];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  AgentDefinition,
  AgentDefinitionQuerySpec
>;
export type AgentDefinitionQuery = {
  /** 
  Cursor paging options.

  Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.

  Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object.

  Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section). 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: /*#__PURE__*/ createQueryUtils<
    AgentDefinition,
    AgentDefinitionQuerySpec,
    AgentDefinitionQuery
  >(),
};

/**
 * Synchronously updates tags on multiple AgentDefinitions by ID.
 * @param agentDefinitionIds - IDs of AgentDefinitions whose tags will be updated.
 * @internal
 * @documentationMaturity preview
 * @requiredField agentDefinitionIds
 * @permissionId agents:v1:agent_definition:bulk_update_agent_definition_tags
 * @fqn wix.agents.v1.AgentRegistryService.BulkUpdateAgentDefinitionTags
 */
export async function bulkUpdateAgentDefinitionTags(
  agentDefinitionIds: string[],
  options?: BulkUpdateAgentDefinitionTagsOptions
): Promise<
  NonNullablePaths<
    BulkUpdateAgentDefinitionTagsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    4
  > & {
    __applicationErrorsType?: BulkUpdateAgentDefinitionTagsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    agentDefinitionIds: agentDefinitionIds,
    assignTags: options?.assignTags,
    unassignTags: options?.unassignTags,
  });

  const reqOpts =
    ambassadorWixAgentsV1AgentDefinition.bulkUpdateAgentDefinitionTags(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          agentDefinitionIds: '$[0]',
          assignTags: '$[1].assignTags',
          unassignTags: '$[1].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['agentDefinitionIds', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateAgentDefinitionTagsOptions {
  /** Tags to assign. */
  assignTags?: Tags;
  /** Tags to unassign. */
  unassignTags?: Tags;
}

/**
 * Asynchronously updates tags on AgentDefinitions matching the provided filter.
 * @param filter - Filter that selects AgentDefinitions whose tags will be updated.
 * @internal
 * @documentationMaturity preview
 * @requiredField filter
 * @permissionId agents:v1:agent_definition:bulk_update_agent_definition_tags_by_filter
 * @fqn wix.agents.v1.AgentRegistryService.BulkUpdateAgentDefinitionTagsByFilter
 */
export async function bulkUpdateAgentDefinitionTagsByFilter(
  filter: Record<string, any>,
  options?: BulkUpdateAgentDefinitionTagsByFilterOptions
): Promise<
  NonNullablePaths<
    BulkUpdateAgentDefinitionTagsByFilterResponse,
    `jobId`,
    0
  > & {
    __applicationErrorsType?: BulkUpdateAgentDefinitionTagsByFilterApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: filter,
    assignTags: options?.assignTags,
    unassignTags: options?.unassignTags,
  });

  const reqOpts =
    ambassadorWixAgentsV1AgentDefinition.bulkUpdateAgentDefinitionTagsByFilter(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          filter: '$[0]',
          assignTags: '$[1].assignTags',
          unassignTags: '$[1].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['filter', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateAgentDefinitionTagsByFilterOptions {
  /** Tags to assign. */
  assignTags?: Tags;
  /** Tags to unassign. */
  unassignTags?: Tags;
}
